import { useEffect, useState } from "react";
import {
  getNodeDetail,
  type KnowledgeNodeDetail,
  type KnowledgeNodeRecord,
} from "@/api/memory";
import { listWorkSkills } from "@/api/work-skills";
import { listSkills, type Skill } from "@/api/agents";

export function useSkillPage(skillId: string, kind?: string) {
  const [detail, setDetail] = useState<KnowledgeNodeDetail | null>(null);
  const [agent, setAgent] = useState<Skill | null>(null);
  const [skills, setSkills] = useState<KnowledgeNodeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    setDetail(null);
    setAgent(null);
    async function load() {
      if (!skillId) throw new Error("Missing skill");
      if (kind === "agent") {
        const item = (await listSkills()).find((entry) => entry.id === skillId);
        if (!item) throw new Error("Unavailable");
        if (current) setAgent(item);
      } else {
        const items = await listWorkSkills();
        const item = items.find((entry) => entry.id === skillId);
        if (!item) throw new Error("Unavailable");
        const full = await getNodeDetail(skillId);
        if (full.id !== skillId) throw new Error("Unavailable");
        if (current) {
          setSkills(items);
          setDetail(full);
        }
      }
    }
    void load()
      .catch(() => {
        if (current) setError(true);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [skillId, kind, revision]);
  const library =
    detail && typeof detail.metadata.adapts === "string"
      ? skills.find(
          (item) =>
            item.shared &&
            `${item.metadata.external_id}@${item.metadata.version}` ===
              detail.metadata.adapts,
        )
      : undefined;
  const personal = detail?.shared
    ? skills.find(
        (item) =>
          !item.shared &&
          item.metadata.adapts ===
            `${detail.metadata.external_id}@${detail.metadata.version}`,
      )
    : undefined;
  return {
    detail,
    setDetail,
    agent,
    library,
    personal,
    loading,
    error,
    retry: () => setRevision((value) => value + 1),
  };
}
